import React, { useState, useEffect, useCallback } from 'react';
import SelectBuscable from '../components/SelectBuscable';
import { plural } from '../lib/plurales';
import { useCompany } from '../CompanyContext';
import { useImpersonation } from '../ImpersonationContext';
import { hasPlanAccess, PLAN_FEATURES } from '../lib/pricing';
import UpgradeWall from '../components/UpgradeWall';
import LuminairePlantView from '../components/LuminairePlantView';
import {
  calculateFullReport,
  EM_TABLE_ART10,
  RoomInput,
  ReflectanceInput,
  TipoTareaArt10,
  IluminacionFullReport,
  notaSobreRangoArt10,
} from '../lib/iluminacion';
import {
  fetchLuminaireCatalog,
  fetchLightingCalculations,
  createLightingCalculation,
  CATEGORIAS_LUMINARIA,
  AVISO_AREA_CLASIFICADA,
  textoDistribuidorEcuador,
  LuminaireCatalogItem,
  LightingCalculationRow,
} from '../lib/gestion-tecnica-iluminacion';
import { fetchJobPositionsFull, JobPositionLegalFields } from '../lib/gestion-tecnica-constructor';
import { generateLightingReportPDF } from '../lib/gestion-tecnica-iluminacion-pdf';
import {
  estimateDirectUniformity,
  formatRatioTruncadoEs,
  presentarUniformidadDirecta,
  UNIFORMIDAD_DIRECTA_NOTA,
  ORIENTACION_FOTOMETRIA_NOTA,
  DirectUniformityResult,
} from '../lib/ies-photometric';

const inputStyle: React.CSSProperties = {
  width: '100%', background: '#0f172a', border: '1px solid #334155',
  borderRadius: 6, color: '#e2e8f0', padding: '8px 10px', marginTop: 4,
};

const labelStyle: React.CSSProperties = {
  display: 'block', marginBottom: 4, color: '#e2e8f0', fontWeight: 600, fontSize: 13,
};

const LuminairePlant3DView = React.lazy(() => import('../components/LuminairePlant3DView'));

export default function IluminacionMethodologyPage() {
  const { company, profile } = useCompany();
  const { impersonated }     = useImpersonation();
  const displayCompany       = impersonated ?? company;
  const companyId            = displayCompany?.id ?? '';
  const isSuperAdmin         = profile?.role === 'superadmin';

  // ── Plan gate (after all hooks) ──
  const planAllowed = isSuperAdmin || !displayCompany || hasPlanAccess(displayCompany.plan, 'metodologia-iluminacion');

  const [jobPositions, setJobPositions] = useState<JobPositionLegalFields[]>([]);
  const [luminaireCatalog, setLuminaireCatalog] = useState<LuminaireCatalogItem[]>([]);
  const [savedCalculations, setSavedCalculations] = useState<LightingCalculationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ── Estado del formulario ──
  const [nombreLocal, setNombreLocal] = useState('');
  const [selectedJobPositionId, setSelectedJobPositionId] = useState('');

  const [largo, setLargo] = useState(10);
  const [ancho, setAncho] = useState(10);
  const [altura, setAltura] = useState(3);
  const [alturaPlanoTrabajo, setAlturaPlanoTrabajo] = useState(0.75);

  const [techo, setTecho] = useState(70);
  const [paredes, setParedes] = useState(50);
  const [piso, setPiso] = useState(20);

  const [tipoTarea, setTipoTarea] = useState<TipoTareaArt10>('tareas_generales_oficina');
  const [ambiente, setAmbiente] = useState<'limpio' | 'sucio'>('limpio');
  const [selectedLuminaireId, setSelectedLuminaireId] = useState('');
  const [coeficienteUtilizacionManual, setCoeficienteUtilizacionManual] = useState<number | undefined>(undefined);

  const [guardando, setGuardando] = useState(false);
  const [guardadoOk, setGuardadoOk] = useState(false);
  const [ultimoCalculoGuardado, setUltimoCalculoGuardado] = useState<LightingCalculationRow | null>(null);
  const [descargandoPdf, setDescargandoPdf] = useState(false);
  const [customPositions, setCustomPositions] = useState<Record<number, { xMeters: number; yMeters: number }>>({});
  const [showComparacion, setShowComparacion] = useState(false);
  const [selectedLuminaireIdB, setSelectedLuminaireIdB] = useState('');
  // Filtro por categoría de cada selector ('' = todas). Solo filtra la lista.
  const [categoriaA, setCategoriaA] = useState('');
  const [categoriaB, setCategoriaB] = useState('');
  const [vista3D, setVista3D] = useState(false);

  useEffect(() => {
    setCustomPositions({});
  }, [largo, ancho, altura, alturaPlanoTrabajo, selectedLuminaireId, tipoTarea, ambiente, coeficienteUtilizacionManual]);

  const loadData = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const [positions, catalog, calculations] = await Promise.all([
        fetchJobPositionsFull(companyId),
        fetchLuminaireCatalog(),
        fetchLightingCalculations(companyId),
      ]);
      setJobPositions(positions);
      setLuminaireCatalog(catalog);
      setSavedCalculations(calculations);
    } catch (e: any) {
      setError(e.message ?? 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { loadData(); }, [loadData]);

  if (!planAllowed) {
    return <UpgradeWall {...PLAN_FEATURES['metodologia-iluminacion']} />;
  }

  if (loading) return <div style={{ padding: 24, color: '#94a3b8' }}>Cargando…</div>;

  const room: RoomInput = { largo, ancho, altura, alturaPlanoTrabajo };
  const reflectances: ReflectanceInput = { techo, paredes, piso };
  const selectedLuminaire = luminaireCatalog.find(l => l.id === selectedLuminaireId);
  const selectedLuminaireB = luminaireCatalog.find(l => l.id === selectedLuminaireIdB);
  const categoriasDisponibles = CATEGORIAS_LUMINARIA.filter(c => luminaireCatalog.some(l => l.categoria === c));
  const porCategoria = (cat: string) => (cat ? luminaireCatalog.filter(l => l.categoria === cat) : luminaireCatalog);

  const puedeCalcular = largo > 0 && ancho > 0 && altura > 0 && selectedLuminaire !== undefined;
  const resultado: IluminacionFullReport | null = puedeCalcular
    ? calculateFullReport(
        room,
        reflectances,
        {
          lumenesPorLampara: selectedLuminaire!.total_lumens,
          cantidadLamparasPorLuminaria: selectedLuminaire!.num_lamps,
          factorPerdidaLuz: 1,
          tipoDistribucion: 'directa', // TODO: agregar selector de tipo de distribución en un ajuste posterior; por ahora default razonable
          coeficienteUtilizacion: coeficienteUtilizacionManual,
        },
        tipoTarea,
        ambiente
      )
    : null;

  const puedeCalcularB = largo > 0 && ancho > 0 && altura > 0 && selectedLuminaireB !== undefined;
  const resultadoB: IluminacionFullReport | null = puedeCalcularB
    ? calculateFullReport(
        room,
        reflectances,
        {
          lumenesPorLampara: selectedLuminaireB!.total_lumens,
          cantidadLamparasPorLuminaria: selectedLuminaireB!.num_lamps,
          factorPerdidaLuz: 1,
          tipoDistribucion: 'directa',
          // Sin CU manual para B — siempre usa el fallback estimado, para no
          // complicar el formulario con un segundo campo de CU.
        },
        tipoTarea,
        ambiente
      )
    : null;

  // Uniformidad Uo = Emín/Epromedio (EN 12464-1:2021 por remisión del DE 255 Art. 48) — indicador INFORMATIVO, no
  // interviene en cumpleNormativa. Usa las mismas posiciones que la planta
  // 2D/3D (grilla + reposicionamientos manuales). La luminaria B no tiene
  // planta propia: se evalúa con su grilla calculada, sin reposicionamientos.
  const uniformidadA: DirectUniformityResult | null = resultado
    ? estimateDirectUniformity(room, resultado.espaciado, selectedLuminaire, customPositions)
    : null;
  const uniformidadB: DirectUniformityResult | null = resultadoB
    ? estimateDirectUniformity(room, resultadoB.espaciado, selectedLuminaireB)
    : null;
  const presA = uniformidadA ? presentarUniformidadDirecta(uniformidadA.uo, tipoTarea) : null;
  const presB = uniformidadB ? presentarUniformidadDirecta(uniformidadB.uo, tipoTarea) : null;
  // Nota neutra si se supera el máximo del rango del Art. 10.4 (no afecta cumpleNormativa).
  const notaRangoA = resultado ? notaSobreRangoArt10(resultado.iluminanciaResultante, resultado.emRequerido) : null;
  const notaRangoB = resultadoB ? notaSobreRangoArt10(resultadoB.iluminanciaResultante, resultadoB.emRequerido) : null;
  const hayAreaClasificada =
    selectedLuminaire?.categoria === 'Área clasificada' || (!!resultadoB && selectedLuminaireB?.categoria === 'Área clasificada');

  const handleGuardar = async () => {
    setError(null);
    if (!nombreLocal.trim()) { setError('Ingresa un nombre para el local'); return; }
    if (!resultado) { setError('Completa el formulario para poder calcular antes de guardar'); return; }

    setGuardando(true);
    setGuardadoOk(false);
    try {
      await createLightingCalculation({
        companyId,
        jobPositionId: selectedJobPositionId || undefined,
        nombreLocal,
        tipoTareaArt10: tipoTarea,
        inputs: { room, reflectances, luminaire: selectedLuminaire, coeficienteUtilizacionManual, ambiente, customPositions },
        resultado,
        cumpleNormativa: resultado.cumpleNormativa,
        createdBy: profile?.id ?? null,
      });
      // createLightingCalculation() no retorna el registro insertado (mismo patrón
      // que createBiologicalExposure()) — se vuelve a consultar para obtener el
      // registro recién creado con su id real, necesario para generar el PDF.
      const calculations = await fetchLightingCalculations(companyId);
      setSavedCalculations(calculations);
      setUltimoCalculoGuardado(calculations[0] ?? null);
      setGuardadoOk(true);
    } catch (e: any) {
      setError(e.message ?? 'Error al guardar el cálculo');
    } finally {
      setGuardando(false);
    }
  };

  const handleDescargarPdf = async () => {
    if (!ultimoCalculoGuardado) {
      setError('No hay un cálculo guardado para generar el PDF');
      return;
    }
    setDescargandoPdf(true);
    try {
      await generateLightingReportPDF(
        ultimoCalculoGuardado,
        companyId,
        displayCompany?.name ?? 'Empresa',
        profile?.id ?? null
      );
    } catch (e: any) {
      setError(e.message ?? 'Error al generar el PDF');
    } finally {
      setDescargandoPdf(false);
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 900 }}>
      <h1 style={{ fontSize: 22, marginBottom: 6, color: '#e2e8f0' }}>Metodología de Iluminación (Art. 10 - Anexo 3 AM 196)</h1>
      <p style={{ fontSize: 13, color: '#94a3b8', marginBottom: 20 }}>
        Cálculo de iluminación según el método de los lúmenes (CITCEA-UPC), conforme al Art. 10 del Anexo 3 - AM 196.
      </p>

      {error && (
        <div style={{ background: '#7f1d1d', color: '#fecaca', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13 }}>
          {error}
        </div>
      )}

      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Nombre del local</label>
        <input
          style={inputStyle}
          type="text"
          value={nombreLocal}
          onChange={e => setNombreLocal(e.target.value)}
          placeholder="Ej. Bodega principal, Oficina administrativa..."
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Puesto de trabajo (opcional)</label>
        <SelectBuscable inputStyle={{ ...inputStyle, paddingRight: 32 }} ariaLabel="Puesto de trabajo (opcional)" value={selectedJobPositionId}
          onChange={setSelectedJobPositionId} vacio="— Sin asociar —" opciones={jobPositions.map(p => ({ value: p.id, label: p.name }))} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
        <div>
          <label style={labelStyle}>Largo (m)</label>
          <input style={inputStyle} type="number" value={largo} onChange={e => setLargo(parseFloat(e.target.value) || 0)} />
        </div>
        <div>
          <label style={labelStyle}>Ancho (m)</label>
          <input style={inputStyle} type="number" value={ancho} onChange={e => setAncho(parseFloat(e.target.value) || 0)} />
        </div>
        <div>
          <label style={labelStyle}>Altura (m)</label>
          <input style={inputStyle} type="number" value={altura} onChange={e => setAltura(parseFloat(e.target.value) || 0)} />
        </div>
        <div>
          <label style={labelStyle}>Altura plano de trabajo (m)</label>
          <input style={inputStyle} type="number" value={alturaPlanoTrabajo} onChange={e => setAlturaPlanoTrabajo(parseFloat(e.target.value) || 0)} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
        <div>
          <label style={labelStyle}>Reflectancia techo (%)</label>
          <input style={inputStyle} type="number" value={techo} onChange={e => setTecho(parseFloat(e.target.value) || 0)} />
        </div>
        <div>
          <label style={labelStyle}>Reflectancia paredes (%)</label>
          <input style={inputStyle} type="number" value={paredes} onChange={e => setParedes(parseFloat(e.target.value) || 0)} />
        </div>
        <div>
          <label style={labelStyle}>Reflectancia piso (%)</label>
          <input style={inputStyle} type="number" value={piso} onChange={e => setPiso(parseFloat(e.target.value) || 0)} />
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Tipo de tarea (Art. 10.4)</label>
        <select style={inputStyle} value={tipoTarea} onChange={e => setTipoTarea(e.target.value as TipoTareaArt10)}>
          {(Object.keys(EM_TABLE_ART10) as TipoTareaArt10[]).map(key => (
            <option key={key} value={key} style={{ color: '#000000' }}>{EM_TABLE_ART10[key].label}</option>
          ))}
        </select>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Ambiente</label>
        <select style={inputStyle} value={ambiente} onChange={e => setAmbiente(e.target.value as 'limpio' | 'sucio')}>
          <option value="limpio" style={{ color: '#000000' }}>Limpio</option>
          <option value="sucio" style={{ color: '#000000' }}>Sucio</option>
        </select>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Luminaria</label>
        <select
          data-testid="filtro-categoria-a"
          style={{ ...inputStyle, marginBottom: 6 }}
          value={categoriaA}
          onChange={e => {
            setCategoriaA(e.target.value);
            // Si la luminaria elegida no es de la nueva categoría, se deselecciona.
            if (e.target.value && selectedLuminaire?.categoria !== e.target.value) setSelectedLuminaireId('');
          }}
        >
          <option value="" style={{ color: '#000000' }}>Todas las categorías</option>
          {categoriasDisponibles.map(c => <option key={c} value={c} style={{ color: '#000000' }}>{c}</option>)}
        </select>
        <select style={inputStyle} value={selectedLuminaireId} onChange={e => setSelectedLuminaireId(e.target.value)}>
          <option value="" style={{ color: '#000000' }}>— Seleccionar —</option>
          {porCategoria(categoriaA).map(l => (
            <option key={l.id} value={l.id} style={{ color: '#000000' }}>
              {l.family} — {l.lumcat} ({l.total_lumens.toFixed(0)} lm)
            </option>
          ))}
        </select>
        {textoDistribuidorEcuador(selectedLuminaire) && (
          <div data-testid="distribuidor-a" style={{ color: '#94a3b8', fontSize: 12, marginTop: 4 }}>{textoDistribuidorEcuador(selectedLuminaire)}</div>
        )}

        {!showComparacion ? (
          <span
            onClick={() => setShowComparacion(true)}
            style={{ display: 'inline-block', marginTop: 6, color: '#60a5fa', fontSize: 12, cursor: 'pointer' }}
          >
            + Comparar con otra luminaria
          </span>
        ) : (
          <span
            onClick={() => { setShowComparacion(false); setSelectedLuminaireIdB(''); setCategoriaB(''); }}
            style={{ display: 'inline-block', marginTop: 6, color: '#60a5fa', fontSize: 12, cursor: 'pointer' }}
          >
            Quitar comparación
          </span>
        )}
      </div>

      {showComparacion && (
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Luminaria B (comparación)</label>
          <select
            data-testid="filtro-categoria-b"
            style={{ ...inputStyle, marginBottom: 6 }}
            value={categoriaB}
            onChange={e => {
              setCategoriaB(e.target.value);
              if (e.target.value && selectedLuminaireB?.categoria !== e.target.value) setSelectedLuminaireIdB('');
            }}
          >
            <option value="" style={{ color: '#000000' }}>Todas las categorías</option>
            {categoriasDisponibles.map(c => <option key={c} value={c} style={{ color: '#000000' }}>{c}</option>)}
          </select>
          <select style={inputStyle} value={selectedLuminaireIdB} onChange={e => setSelectedLuminaireIdB(e.target.value)}>
            <option value="" style={{ color: '#000000' }}>— Seleccionar —</option>
            {porCategoria(categoriaB).map(l => (
              <option key={l.id} value={l.id} style={{ color: '#000000' }}>
                {l.family} — {l.lumcat} ({l.total_lumens.toFixed(0)} lm)
              </option>
            ))}
          </select>
          {textoDistribuidorEcuador(selectedLuminaireB) && (
            <div data-testid="distribuidor-b" style={{ color: '#94a3b8', fontSize: 12, marginTop: 4 }}>{textoDistribuidorEcuador(selectedLuminaireB)}</div>
          )}
        </div>
      )}

      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>CU manual (opcional — dato real de ficha de fabricante)</label>
        <input
          style={inputStyle}
          type="number"
          step="0.01"
          value={coeficienteUtilizacionManual ?? ''}
          onChange={e => setCoeficienteUtilizacionManual(e.target.value === '' ? undefined : parseFloat(e.target.value))}
          placeholder="Dejar vacío para usar el fallback estimado"
        />
      </div>

      {!puedeCalcular && (
        <div style={{ background: '#1e293b', color: '#94a3b8', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13 }}>
          Completa las dimensiones del local y selecciona una luminaria para ver el cálculo
        </div>
      )}

      {resultado && (
        <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 10, padding: 16, marginBottom: 20 }}>
          <div
            style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, marginBottom: 14,
              background: resultado.cumpleNormativa ? 'rgba(47,191,113,0.12)' : 'rgba(239,68,68,0.12)',
              border: `1px solid ${resultado.cumpleNormativa ? 'rgba(47,191,113,0.3)' : 'rgba(239,68,68,0.3)'}`,
            }}
          >
            <strong style={{ color: resultado.cumpleNormativa ? '#2FBF71' : '#ef4444', fontSize: 14 }}>
              {resultado.cumpleNormativa ? 'CUMPLE' : 'NO CUMPLE'}
            </strong>
            <span style={{ color: '#94a3b8', fontSize: 12 }}>
              — mínimo exigido (Art. 10.4 Anexo 3 AM 196): {resultado.emRequerido.label} · {resultado.emRequerido.min} lux
            </span>
          </div>
          {notaRangoA && (
            <div data-testid="nota-rango-art10" style={{ color: '#94a3b8', fontSize: 12, margin: '-6px 0 14px' }}>{notaRangoA}</div>
          )}
          {hayAreaClasificada && (
            <div data-testid="aviso-area-clasificada" style={{ color: '#94a3b8', fontSize: 12, margin: '0 0 14px', lineHeight: 1.4 }}>
              {AVISO_AREA_CLASIFICADA}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14, fontSize: 13, color: '#e2e8f0' }}>
            <div>
              <span style={{ color: '#94a3b8' }}>Iluminancia resultante</span>
              <div style={{ fontSize: 34, fontWeight: 600 }}>{resultado.iluminanciaResultante.toFixed(1)} lux</div>
            </div>
            <div>
              <span style={{ color: '#94a3b8' }}>Número de luminarias</span>
              <div style={{ fontSize: 34, fontWeight: 600 }}>
                {resultado.numeroLuminariasInstaladas}
              </div>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <span style={{ color: '#94a3b8' }}>Espaciado</span>
              <div>
                {resultado.espaciado.filas} x {resultado.espaciado.columnas} (espaciado {resultado.espaciado.espaciadoX.toFixed(2)}m x {resultado.espaciado.espaciadoY.toFixed(2)}m)
              </div>
            </div>
            {uniformidadA && presA && (
              <div
                data-testid="uniformidad-directa"
                style={{ gridColumn: '1 / -1', background: '#1e293b', border: '1px solid #334155', borderRadius: 8, padding: '10px 14px' }}
              >
                <span style={{ color: '#94a3b8' }}>Uniformidad estimada (luz directa)</span>
                <div style={{ color: presA.color, fontWeight: 600, marginTop: 2 }}>{presA.linea}</div>
                <div style={{ color: presA.color, marginTop: 2 }}>{presA.texto}</div>
                <div style={{ color: '#94a3b8', fontSize: 11.5, marginTop: 4, lineHeight: 1.4 }}>{UNIFORMIDAD_DIRECTA_NOTA}</div>
                <div data-testid="orientacion-fotometria" style={{ color: '#94a3b8', fontSize: 11.5, marginTop: 4, lineHeight: 1.4 }}>{ORIENTACION_FOTOMETRIA_NOTA}</div>
              </div>
            )}
          </div>

          {resultado && resultadoB && selectedLuminaire && selectedLuminaireB && (
            <div style={{ marginBottom: 14 }}>
              <span style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 6 }}>Comparación de luminarias</span>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, color: '#e2e8f0' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #334155', textAlign: 'left' }}>
                    <th style={{ padding: '6px 8px' }}>Luminaria</th>
                    <th style={{ padding: '6px 8px' }}>Iluminancia</th>
                    <th style={{ padding: '6px 8px' }}>Cumple</th>
                    <th style={{ padding: '6px 8px' }}>N° luminarias</th>
                    <th style={{ padding: '6px 8px' }}>Watts totales</th>
                    <th style={{ padding: '6px 8px' }} title={UNIFORMIDAD_DIRECTA_NOTA}>Emín/Epromedio (directa)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderBottom: '1px solid #1e293b' }}>
                    <td style={{ padding: '6px 8px' }}>A: {selectedLuminaire.lumcat}</td>
                    <td style={{ padding: '6px 8px' }}>
                      {resultado.iluminanciaResultante.toFixed(1)} lux
                      {notaRangoA && <div data-testid="nota-rango-art10-a" style={{ color: '#94a3b8', fontSize: 11 }}>{notaRangoA}</div>}
                    </td>
                    <td style={{ padding: '6px 8px' }}>{resultado.cumpleNormativa ? '✓' : '✗'}</td>
                    <td style={{ padding: '6px 8px' }}>{resultado.numeroLuminariasInstaladas}</td>
                    <td style={{ padding: '6px 8px' }}>{(selectedLuminaire.input_wattage * resultado.numeroLuminariasInstaladas).toFixed(0)} W</td>
                    <td data-testid="uniformidad-ab-a" style={{ padding: '6px 8px', color: presA?.color }} title={presA ? `${presA.linea}. ${presA.texto}` : undefined}>
                      {uniformidadA && presA ? `${formatRatioTruncadoEs(uniformidadA.uo)} · ${presA.etiquetaCorta}` : '—'}
                    </td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #1e293b' }}>
                    <td style={{ padding: '6px 8px' }}>B: {selectedLuminaireB.lumcat}</td>
                    <td style={{ padding: '6px 8px' }}>
                      {resultadoB.iluminanciaResultante.toFixed(1)} lux
                      {notaRangoB && <div data-testid="nota-rango-art10-b" style={{ color: '#94a3b8', fontSize: 11 }}>{notaRangoB}</div>}
                    </td>
                    <td style={{ padding: '6px 8px' }}>{resultadoB.cumpleNormativa ? '✓' : '✗'}</td>
                    <td style={{ padding: '6px 8px' }}>{resultadoB.numeroLuminariasInstaladas}</td>
                    <td style={{ padding: '6px 8px' }}>{(selectedLuminaireB.input_wattage * resultadoB.numeroLuminariasInstaladas).toFixed(0)} W</td>
                    <td data-testid="uniformidad-ab-b" style={{ padding: '6px 8px', color: presB?.color }} title={presB ? `${presB.linea}. ${presB.texto}` : undefined}>
                      {uniformidadB && presB ? `${formatRatioTruncadoEs(uniformidadB.uo)} · ${presB.etiquetaCorta}` : '—'}
                    </td>
                  </tr>
                </tbody>
              </table>
              <div data-testid="orientacion-fotometria-ab" style={{ color: '#94a3b8', fontSize: 11.5, marginTop: 6, lineHeight: 1.4 }}>{ORIENTACION_FOTOMETRIA_NOTA}</div>
            </div>
          )}

          <div style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <button
                onClick={() => setVista3D(false)}
                style={{
                  background: !vista3D ? '#334155' : 'transparent', color: '#e2e8f0', border: '1px solid #334155',
                  borderRadius: 6, padding: '6px 14px', fontSize: 12, cursor: 'pointer',
                }}
              >
                Planta 2D
              </button>
              <button
                onClick={() => setVista3D(true)}
                style={{
                  background: vista3D ? '#334155' : 'transparent', color: '#e2e8f0', border: '1px solid #334155',
                  borderRadius: 6, padding: '6px 14px', fontSize: 12, cursor: 'pointer',
                }}
              >
                Vista 3D
              </button>
            </div>

            {!vista3D ? (
              <LuminairePlantView
                room={room}
                espaciado={resultado.espaciado}
                numeroLuminariasInstaladas={resultado.numeroLuminariasInstaladas}
                customPositions={customPositions}
                onPositionChange={(index, xMeters, yMeters) => {
                  setCustomPositions(prev => ({ ...prev, [index]: { xMeters, yMeters } }));
                }}
              />
            ) : (
              <React.Suspense
                fallback={
                  <div style={{ height: 420, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 13, background: '#0f172a', borderRadius: 8, border: '1px solid #334155' }}>
                    Cargando vista 3D…
                  </div>
                }
              >
                <LuminairePlant3DView
                  room={room}
                  espaciado={resultado.espaciado}
                  numeroLuminariasInstaladas={resultado.numeroLuminariasInstaladas}
                  customPositions={customPositions}
                />
              </React.Suspense>
            )}

            {Object.keys(customPositions).length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, fontSize: 12, color: '#94a3b8' }}>
                <span>{plural(Object.keys(customPositions).length, 'luminaria reposicionada', 'luminarias reposicionadas')} manualmente</span>
                <button
                  onClick={() => setCustomPositions({})}
                  style={{
                    background: 'transparent', color: '#60a5fa', border: '1px solid #334155',
                    borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer',
                  }}
                >
                  Restablecer posiciones
                </button>
              </div>
            )}
          </div>

          {resultado.fuenteFactorUtilizacion === 'estimado' ? (
            <div style={{ background: '#78350f', color: '#fde68a', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 12 }}>
              ⚠️ CU estimado — valor orientativo sin verificar. Ingresa el CU de la ficha técnica de tu luminaria para mayor precisión.
            </div>
          ) : (
            <div style={{ background: 'rgba(47,191,113,0.12)', color: '#2FBF71', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 12 }}>
              CU real de ficha técnica de fabricante
            </div>
          )}

          <button
            onClick={handleGuardar}
            disabled={guardando || !puedeCalcular}
            style={{
              background: '#2FBF71', color: '#0f172a', border: 'none', borderRadius: 6,
              padding: '8px 16px', fontWeight: 600, cursor: guardando || !puedeCalcular ? 'not-allowed' : 'pointer',
              opacity: guardando || !puedeCalcular ? 0.6 : 1,
            }}
          >
            {guardando ? 'Guardando...' : 'Guardar cálculo'}
          </button>

          {guardadoOk && (
            <div style={{ color: '#2FBF71', fontSize: 13, marginTop: 10 }}>✓ Cálculo guardado correctamente</div>
          )}

          {guardadoOk && ultimoCalculoGuardado && (
            <button
              onClick={handleDescargarPdf}
              disabled={descargandoPdf}
              style={{
                background: '#3b82f6', color: '#0f172a', border: 'none', borderRadius: 6,
                padding: '8px 16px', fontWeight: 600, cursor: descargandoPdf ? 'not-allowed' : 'pointer',
                opacity: descargandoPdf ? 0.6 : 1, marginTop: 10, marginLeft: 10,
              }}
            >
              {descargandoPdf ? 'Generando PDF...' : 'Descargar reporte PDF'}
            </button>
          )}
        </div>
      )}

      {savedCalculations.length > 0 && (
        <div>
          <h2 style={{ fontSize: 15, color: '#e2e8f0', marginBottom: 8 }}>Cálculos guardados</h2>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, color: '#e2e8f0' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #334155', textAlign: 'left' }}>
                <th style={{ padding: '6px 8px' }}>Local</th>
                <th style={{ padding: '6px 8px' }}>Tipo de tarea</th>
                <th style={{ padding: '6px 8px' }}>Cumple</th>
                <th style={{ padding: '6px 8px' }}>Fecha</th>
              </tr>
            </thead>
            <tbody>
              {savedCalculations.map(c => (
                <tr key={c.id} style={{ borderBottom: '1px solid #1e293b' }}>
                  <td style={{ padding: '6px 8px' }}>{c.nombre_local}</td>
                  <td style={{ padding: '6px 8px' }}>{c.tipo_tarea_art10}</td>
                  <td style={{ padding: '6px 8px' }}>{c.cumple_normativa ? '✓' : '✗'}</td>
                  <td style={{ padding: '6px 8px' }}>{new Date(c.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
